import {
  THEME_LABEL,
  nextThemeChoice,
  type ThemeChoice,
} from "@roof-hawk/core";
import { useEffect, useState, type ReactElement } from "react";

import { t } from "../../i18n";
import { applyThemeChoice, storedThemeChoice } from "../../lib/theme";

/**
 * Light, dark, or whatever the phone is set to.
 *
 * One button rather than a menu, because the top bar is the row of the app
 * that has to survive a phone. It cycles rather than toggling: a two-position
 * switch cannot get you back to "match my phone" once you have left it, and
 * that is the setting a rep who works evenings actually wants.
 *
 * The icon is the state, not the action — a sun means the app is light — with
 * the action in the title and the accessible label, where there is room to say
 * it in words.
 */
export function ThemeButton() {
  const [choice, setChoice] = useState<ThemeChoice>(storedThemeChoice);

  useEffect(() => {
    applyThemeChoice(choice);
  }, [choice]);

  const next = nextThemeChoice(choice);
  const label = t("Theme: {current}. Switch to {next}.", {
    current: t(THEME_LABEL[choice]),
    next: t(THEME_LABEL[next]).toLowerCase(),
  });

  return (
    <button
      type="button"
      className="themebtn"
      title={label}
      aria-label={label}
      onClick={() => setChoice(next)}
    >
      {ICON[choice]}
    </button>
  );
}

const ICON: Record<ThemeChoice, ReactElement> = {
  light: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.6v2.2M12 19.2v2.2M4.2 12H2M22 12h-2.2M6.3 6.3 4.8 4.8M19.2 19.2l-1.5-1.5M17.7 6.3l1.5-1.5M4.8 19.2l1.5-1.5" />
    </svg>
  ),
  dark: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2z" />
    </svg>
  ),
  // Half a sun, half a moon: it follows something else.
  system: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 3.8a8.2 8.2 0 0 0 0 16.4z" fill="currentColor" />
    </svg>
  ),
};
